<template>
  <table class="table table-researcher table-padding-x-0 mb-0">
    <thead>
      <tr>
        <th class="pr-0">Name</th>
        <th></th>
        <th v-if="canView()" class="role-editable"></th>
      </tr>
    </thead>
    <tbody>
      <!-- actual users -->
      <tr v-for="(member) in members" :key="member.slug_values[0]" class="clearfix">
        <td class="avatar">
          <researcher-thumbnail :researcher="member" />
        </td>

        <td>
          <researcher-link :researcher="member" />
          <span v-if="canView()" class="badge badge-pill t-xs badge-secondary--white bd-radius ml-2">
            {{ member.role.name }}
          </span>

          <span v-if="member.contact" class="badge badge-pill t-xs badge-secondary--blue bd-radius ml-2">
            Contact person
          </span>
        </td>

        <td v-if="canView()" class="role-editable text-right">
          <dropdown v-if="canEdit(member)" no-padding>
            <template slot="button">
              <button type="button" class="btn btn-select-menu ">
                <i class="fa fa-cog t-sm mr-2"></i>
              </button>
            </template>
            <!-- render select menu -->
            <select-menu
              :title="`${scope} permissions`"
              :options="getSelectItems(member)"
              @onChange="updateRole(member, $event)"
            ></select-menu>
          </dropdown>
        </td>
      </tr>

      <!-- pending invites -->
      <template v-if="isPendingMemberLoaded">
        <tr v-for="(pm, i) in pendingMembers" :key="i" class="clearfix">
          <td class="avatar">
            <avatar-missing v-if="!pm.user" :name="getFullName(pm)" :maxChars="3" :isSmallClass="false"/>
            <researcher-thumbnail v-if="pm.user" :researcher="pm.user" />
          </td>

          <td>
            <researcher-link v-if="pm.user" :researcher="pm.user" />
            <span v-if="!pm.user">{{ getFullName(pm) }}</span>
            <span class="badge badge-pill t-xs badge-secondary--white bd-radius ml-2">
              Invited - waiting for acceptance
            </span>
          </td>

          <td v-if="canView()"></td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script>
import AvatarMissing from '@/components/shared/AvatarMissing'
import SelectMenu from '@/components/shared/SelectMenu'
import ResearcherLink from '@/components/researcher/Link'
import ResearcherThumbnail from '@/components/researcher/Thumbnail'
import Dropdown from '@/components/shared/form/Dropdown'

export default {
  name: 'crudMember',
  components: {
    AvatarMissing,
    SelectMenu,
    ResearcherLink,
    ResearcherThumbnail,
    Dropdown
  },
  props: {
    organisation: {
      type: Object,
      default: () => {}
    },
    researchGroup: {
      type: Object,
      default: () => {}
    },
    community: {
      type: Object,
      default: () => {}
    },
    members: {
      type: Array,
      required: true
    },
    pendingMembers: {
      type: Array,
      required: true
    },
    isPendingMemberLoaded: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      selectItems: [
        {
          title: 'Permissions',
          rw_access: true,
          key: 'permission',
          description: 'Change admin rights of this member.'
        }, {
          title: 'Contact person',
          key: 'contact',
          rw_access: false
        }, {
          title: 'Remove',
          key: 'remove',
          rw_access: true,
          delete: true
        }
      ]
    }
  },
  created () {
    if (!this.isEmpty(this.researchGroup)) this.scope = 'ResearchGroup'
    if (!this.isEmpty(this.community)) this.scope = 'Community'
    if (!this.isEmpty(this.organisation)) this.scope = 'Organisation'
  },
  computed: {
    rw_access () {
      if (!this.isEmpty(this.researchGroup)) return this.researchGroup.rw_access
      if (!this.isEmpty(this.community)) return this.community.rw_access
      if (!this.isEmpty(this.organisation)) return this.organisation.rw_access
      return ''
    },
    roles () {
      return this.$can.roles()
    }
  },
  methods: {
    getRole (role) {
      return this.roles.filter((arr) => { return arr.key === role })[0].name
    },
    getSelectItems (member) {
      // Prepopulated members are members without a user attached to them.
      // Because of this, they can only be removed
      if (member.prepopulated) {
        return this.selectItems.filter(i => i.key === 'remove').map(j => {
          j.description = `Remove this user from this ${this.$options.filters.lowercase(this.scope)}.`
          return j
        })
      }

      return this.selectItems.map((item) => {
        const newItem = { ...item }
        if (newItem.key === 'remove') {
          if (this.hasMatchingSlug(member.slug_values, this.$currentUser.slug_values)) {
            newItem.title = 'Leave...'
            newItem.description = `Leave this ${this.$options.filters.lowercase(this.scope)}.`
          } else {
            newItem.title = 'Remove...'
            newItem.description = `Remove this user from this ${this.$options.filters.lowercase(this.scope)}.`
          }
        }

        if (newItem.key === 'contact' && member.contact) {
          newItem.description = `Remove this member as contact person for this ${this.$options.filters.lowercase(this.scope)}.`
        } else if (newItem.key === 'contact' && !member.contact) {
          newItem.description = `Add this member as contact person for this ${this.$options.filters.lowercase(this.scope)}.`
        }
        if (!newItem.rw_access) return newItem

        let role = this.roles.filter((r) => { return r.value === this.rw_access })[0]
        if (member.role.id <= role.key) return newItem
      })
    },
    updateRole (member, item) {
      if (item.key === 'contact') this.$emit('updateContact', member, !member.contact)

      if (item.key === 'permission') this.$emit('updateRole', member)

      if (item.key === 'remove') {
        const msg = this.hasMatchingSlug(member.slug_values, this.$currentUser.slug_values)
          ? `Are you sure you want to leave this ${this.$options.filters.lowercase(this.scope)}?`
          : `Are you sure you want to remove ${this.getFullName(member)}?`
        this.$dialog
          .confirm(msg)
          .then((dialog) => {
            dialog.close()
            this.$emit('removeMember', member)
          })
      }
    },
    canView (role) {
      if (this.researchGroup) return this.$can.view('research_group', this.researchGroup.rw_access)
      if (this.community) return this.$can.view('community', this.community.rw_access)
      if (this.organisation) return this.$can.view('organisation', this.organisation.rw_access)
    },
    canEdit (member) {
      if (this.researchGroup) return this.$can.edit('research_group', this.researchGroup.rw_access)
      if (this.community) return this.$can.edit('community', this.community.rw_access)
      if (this.organisation) return this.$can.edit('organisation', this.organisation.rw_access)
    }
  }
}
</script>
